import React, { useEffect, useState } from "react";
import { UserGear, Plus, Trash, LinkSimple, Copy, CheckCircle, XCircle, Ticket, Users } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

export default function AccessControl() {
  const [tab, setTab] = useState("checkers");
  const [events, setEvents] = useState([]);
  const [checkers, setCheckers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", pin: "", event_ids: [], checkpoint_ids: [], category_names: [] });
  // invites & requests
  const [eventId, setEventId] = useState("");
  const [invites, setInvites] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [invOcc, setInvOcc] = useState("");

  const loadBase = () => Promise.all([api.get("/organizer/events"), api.get("/organizer/checkers")])
    .then(([e, c]) => { setEvents(e.data.events); setCheckers(c.data.checkers); if (e.data.events[0]) setEventId((x) => x || e.data.events[0].id); })
    .finally(() => setLoading(false));
  useEffect(() => { loadBase(); }, []);

  const curEvent = events.find((e) => e.id === eventId);
  const loadEventAccess = () => {
    if (!eventId) return;
    api.get(`/organizer/events/${eventId}/invites`).then((r) => setInvites(r.data.invites));
    api.get(`/organizer/events/${eventId}/approvals`).then((r) => { setApprovals(r.data.approvals); setWaitlist(r.data.waitlist); });
    if (curEvent?.occurrences?.[0]) setInvOcc(curEvent.occurrences[0].id);
  };
  useEffect(() => { if (tab === "invites") loadEventAccess(); }, [eventId, tab]); // eslint-disable-line

  const selectedCheckpoints = () => {
    const ids = new Set();
    events.filter((e) => form.event_ids.includes(e.id)).forEach((e) => (e.checkpoints || []).forEach((c) => ids.add(JSON.stringify({ id: c.id, name: c.name }))));
    return [...ids].map((s) => JSON.parse(s));
  };

  const selectedCategories = () => {
    const names = new Set();
    events.filter((e) => form.event_ids.includes(e.id)).forEach((e) =>
      (e.occurrences || []).forEach((o) => (o.ticket_categories || []).forEach((c) => c.name && names.add(c.name))));
    return [...names];
  };

  const toggleEvent = (id) => setForm((f) => ({ ...f, event_ids: f.event_ids.includes(id) ? f.event_ids.filter((x) => x !== id) : [...f.event_ids, id] }));
  const toggleCp = (id) => setForm((f) => ({ ...f, checkpoint_ids: f.checkpoint_ids.includes(id) ? f.checkpoint_ids.filter((x) => x !== id) : [...f.checkpoint_ids, id] }));
  const toggleCat = (n) => setForm((f) => ({ ...f, category_names: f.category_names.includes(n) ? f.category_names.filter((x) => x !== n) : [...f.category_names, n] }));

  const createChecker = async () => {
    try {
      await api.post("/organizer/checkers", form);
      toast.success("Checker created.");
      setOpen(false); setForm({ name: "", email: "", pin: "", event_ids: [], checkpoint_ids: [], category_names: [] });
      loadBase();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delChecker = async (id) => { try { await api.delete(`/organizer/checkers/${id}`); toast.success("Removed."); loadBase(); } catch (e) { toast.error(apiErr(e)); } };

  const genInvite = async () => {
    try { await api.post(`/organizer/events/${eventId}/invites`, { occurrence_id: invOcc, max_uses: null }); toast.success("Invite link generated."); loadEventAccess(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const copyInvite = (t) => { navigator.clipboard.writeText(`${window.location.origin}/events/${eventId}?invite=${t}`); toast.success("Invite link copied."); };
  const revokeInvite = async (id) => { try { await api.delete(`/organizer/invites/${id}`); toast.success("Revoked."); loadEventAccess(); } catch (e) { toast.error(apiErr(e)); } };
  const actApproval = async (rid, action) => { try { const { data } = await api.post(`/organizer/approvals/${rid}/${action}`); toast.success(data.message); loadEventAccess(); } catch (e) { toast.error(apiErr(e)); } };

  const eventName = (id) => events.find((e) => e.id === id)?.title || id;

  if (loading) return <PageLoader />;

  return (
    <div className="max-w-4xl">
      <div className="overline mb-1">Team & access</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Checkers & Access</h1>

      <div className="flex gap-2 mb-6 p-1 rounded-full bg-muted w-fit">
        {[["checkers", "Ticket Checkers"], ["invites", "Invites & Requests"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`access-tab-${v}`}
            className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "checkers" ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">Staff sign in at <b>/scan</b> with their email + PIN to scan tickets.</p>
            <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="add-checker-btn"><Plus className="mr-1.5" /> Add checker</Button>
          </div>
          {checkers.length === 0 ? (
            <EmptyState icon={UserGear} title="No checkers yet" subtitle="Add gate staff and assign them to events and checkpoints." testid="no-checkers" />
          ) : (
            <div className="space-y-3">
              {checkers.map((c) => (
                <div key={c.id} className="p-4 rounded-xl border border-border bg-card flex items-center justify-between" data-testid={`checker-${c.id}`}>
                  <div>
                    <div className="font-semibold">{c.name} <span className="text-xs text-muted-foreground">· {c.email}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">PIN {c.pin} · {c.event_ids.length} event(s) · {c.checkpoint_ids?.length ? `${c.checkpoint_ids.length} checkpoint(s)` : "all checkpoints"} · {c.category_names?.length ? c.category_names.join(", ") : "all categories"}</div>
                  </div>
                  <button onClick={() => delChecker(c.id)} className="text-destructive" data-testid={`del-checker-${c.id}`}><Trash size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="max-w-sm mb-5">
            <Label className="mb-1.5 block">Event</Label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger data-testid="access-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 mb-6">
            <div className="flex items-center gap-2 mb-3"><LinkSimple size={20} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Private invite links</h2></div>
            <div className="flex flex-wrap gap-2 items-end mb-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs mb-1 block">Occurrence</Label>
                <Select value={invOcc} onValueChange={setInvOcc}>
                  <SelectTrigger data-testid="invite-occ-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(curEvent?.occurrences || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.date} · {o.city} ({o.mode})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={genInvite} className="rounded-full" data-testid="gen-invite-btn"><Plus className="mr-1.5" /> Generate link</Button>
            </div>
            {invites.length === 0 ? <p className="text-sm text-muted-foreground">No invite links yet.</p> : (
              <div className="space-y-2">
                {invites.map((iv) => (
                  <div key={iv.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 text-sm" data-testid={`invite-${iv.id}`}>
                    <span className="font-mono text-xs truncate max-w-[45%]">…{iv.token.slice(-10)}</span>
                    <span className="text-xs text-muted-foreground">used {iv.used_count}{iv.max_uses ? `/${iv.max_uses}` : ""}</span>
                    <div className="flex gap-2">
                      <button onClick={() => copyInvite(iv.token)} className="text-primary" data-testid={`copy-invite-${iv.id}`}><Copy size={15} /></button>
                      <button onClick={() => revokeInvite(iv.id)} className="text-destructive"><Trash size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5 mb-6">
            <div className="flex items-center gap-2 mb-3"><CheckCircle size={20} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Attendance requests</h2></div>
            {approvals.length === 0 ? <p className="text-sm text-muted-foreground">No requests yet.</p> : (
              <div className="space-y-2">
                {approvals.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`approval-${r.id}`}>
                    <div>
                      <div className="font-medium text-sm">{r.user_name} <span className="text-xs text-muted-foreground">· {r.user_email}</span></div>
                      <div className="text-xs text-muted-foreground">{r.occurrence_label} · qty {r.qty}</div>
                    </div>
                    {r.status === "pending" ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => actApproval(r.id, "approve")} className="rounded-full h-8" data-testid={`approve-${r.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => actApproval(r.id, "reject")} className="rounded-full h-8">Reject</Button>
                      </div>
                    ) : <StatusBadge status={(r.status === "booked" || r.status === "paid") ? "confirmed" : r.status === "approved" ? "approved" : "rejected"} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3"><Users size={20} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Waitlist</h2></div>
            {waitlist.length === 0 ? <p className="text-sm text-muted-foreground">Nobody on the waitlist.</p> : (
              <div className="space-y-2">
                {waitlist.map((w) => (
                  <div key={w.id} className="flex items-center justify-between p-3 rounded-lg border border-border text-sm" data-testid={`waitlist-${w.id}`}>
                    <span>{w.user_name} · {w.category_name}</span>
                    <span className="text-xs text-muted-foreground">{w.occurrence_label} · qty {w.qty}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="checker-dialog">
          <DialogHeader><DialogTitle>Add ticket checker</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="checker-name-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="checker-email-input" /></div>
            <div><Label>PIN (4-6 digits)</Label><Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="mt-1.5 font-mono" data-testid="checker-pin-input" /></div>
            <div>
              <Label className="mb-1.5 block">Assign to events</Label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {events.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-border cursor-pointer">
                    <input type="checkbox" checked={form.event_ids.includes(e.id)} onChange={() => toggleEvent(e.id)} data-testid={`checker-event-${e.id}`} />
                    {e.title}
                  </label>
                ))}
              </div>
            </div>
            {selectedCheckpoints().length > 0 && (
              <div>
                <Label className="mb-1.5 block">Checkpoints (leave empty for all)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCheckpoints().map((c) => {
                    const on = form.checkpoint_ids.includes(c.id);
                    return <button key={c.id} type="button" onClick={() => toggleCp(c.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-background text-muted-foreground border-border"}`}>{c.name}</button>;
                  })}
                </div>
              </div>
            )}
            {selectedCategories().length > 0 && (
              <div>
                <Label className="mb-1.5 block">Ticket categories they can scan (empty = all)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCategories().map((n) => {
                    const on = form.category_names.includes(n);
                    return <button key={n} type="button" onClick={() => toggleCat(n)} data-testid={`checker-cat-${n}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? "bg-primary/10 text-primary border-primary/40" : "bg-background text-muted-foreground border-border"}`}>{n}</button>;
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">e.g. a VIP Gate checker scanning a Regular ticket will be blocked with "Category not authorized".</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={createChecker} className="w-full rounded-full" data-testid="submit-checker">Create checker</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
